import { Controller } from '@hotwired/stimulus'
import { debounce } from 'throttle-debounce'

type FormControl = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement

interface AmountChangeEvent extends Event {
  detail?: {
    rawValue?: string
  }
}

type Bucket = 'enough-cash' | 'insufficient-cash' | 'insufficient-value'

// Shows or hides sections of the partial-distribution card as the requested
// gross amount is compared against the account's cash balance and total value.
// Modelled on `input-reveal`: each `revealable` names the bucket it belongs to
// with `data-amount-threshold-for`, and a hidden fieldset is disabled so its
// inputs stay out of the submitted form.
export default class AmountThresholdController extends Controller<HTMLElement> {
  declare accountValue: number
  declare amountValue: number
  declare cashValue: number
  declare debouncedHandleChange: ReturnType<typeof debounce>
  declare readonly amountInputTarget: HTMLInputElement
  declare readonly hasAccountValue: boolean
  declare readonly hasCashValue: boolean
  declare readonly revealableTargets: HTMLElement[]

  static targets = ['amountInput', 'revealable']
  static values = { account: Number, amount: Number, cash: Number }

  // This exists to store the live amount as the user types. We use this instead
  // of a normal Stimulus value to avoid Stimulus updating data attributes in
  // the DOM after each keystroke that the user fires as they type.
  // Triggered by the input's `currency-input:changed` event.
  #amount = 0

  // Getters

  get activeBuckets(): Bucket[] {
    const buckets: Bucket[] = []

    if (this.hasCashValue) {
      buckets.push(this.cashValue >= this.#amount ? 'enough-cash' : 'insufficient-cash')
    }

    if (this.hasAccountValue && this.accountValue < this.#amount) {
      buckets.push('insufficient-value')
    }

    return buckets
  }

  // Lifecycle methods

  connect() {
    this.#amount = this.amountValue
    this.#render()
    this.dispatch('connect')
  }

  amountInputTargetConnected(element: HTMLInputElement) {
    this.debouncedHandleChange = debounce(50, this.#handleChange)
    element.addEventListener('currency-input:changed', this.debouncedHandleChange)
  }

  amountInputTargetDisconnected(element: HTMLInputElement) {
    element.removeEventListener('currency-input:changed', this.debouncedHandleChange)
    this.debouncedHandleChange.cancel()
  }

  // Private methods

  #handleChange = (event: AmountChangeEvent) => {
    const { rawValue } = event.detail ?? {}
    this.#amount = Number.parseFloat(rawValue ?? '') || 0
    this.#render()
  }

  #render() {
    const active = this.activeBuckets

    for (const revealable of this.revealableTargets) {
      const isHidden = !active.some(bucket => bucket === revealable.dataset.amountThresholdFor)

      revealable.classList.toggle('hidden', isHidden)

      if (revealable instanceof HTMLFieldSetElement) {
        revealable.disabled = isHidden
      } else {
        revealable.querySelectorAll<FormControl>('input, select, textarea').forEach(input => {
          input.disabled = isHidden
        })
      }
    }
  }
}
